import { useFormikContext } from 'formik';
import Toggle from '../../atoms/Toggle/Toggle';
import { useTranslation } from 'react-i18next';
import { FieldConfig, RequiredType } from '@rabbit/helper';
import { Input } from '../../atoms/inputs/Input/Input';
import { useContext } from 'react';
import { AppContext } from '@rabbit/app-context';
import InputAutoSuggestionWithFreeText from '../../atoms/inputs/Input-Autocomplete';

export interface DynamicInputBuilderProps {
  field: FieldConfig;
  name: string;
  required?: boolean;
}

interface FormValues {
  [key: string]:
    | {
        amount?: string | number;
        currency?: string;
      }
    | any;
}

export const CommonInputBuilder: React.FC<DynamicInputBuilderProps> = ({
  field,
  name,
  required,
}) => {
  const { t } = useTranslation();
  const { setFieldValue, getFieldProps, values } =
    useFormikContext<FormValues>();
  const { tenantInfo } = useContext(AppContext);
  const props = getFieldProps(name);
  const baseStyle = `grid w-full grid-cols-1 gap-3 text-gray-900`;

  const switchInput: Record<string, JSX.Element | null> = {
    'auto-complete-suggestion-text': (
      <div className={`${baseStyle}`}>
        <InputAutoSuggestionWithFreeText
          label={`${field.title} ${required ? '*' : ''}`}
          key={`${name}`}
          name={name}
          settings={{
            id: name,
            placeholder: field.decoration?.placeholder || '',
            suggestion: field.suggestion,
          }}
        />
      </div>
    ),
    currency: (
      <div className={`${baseStyle}`}>
        <Input
          title={field.title}
          label={`${field.title} ${required ? '*' : ''}`}
          key={`${name}`}
          type="currency"
          name={name}
          settings={{
            placeholder: field.decoration?.placeholder || '',
            ...(required && { hint: '*required' }),
            currency: values[name]?.currency || tenantInfo?.currency,
          }}
          className="w-auto"
          infoRender={field.infoRender}
        />
      </div>
    ),
    radio: (
      <div className={`${baseStyle}`}>
        <label>
          {field.title} {required && '*'}
        </label>
        <div className="flex h-auto w-full flex-wrap items-center justify-start gap-4">
          {field.options?.map((value, i) => (
            <Input
              title={value}
              key={i}
              type="radio"
              name={name}
              settings={{
                radioValue: value,
                radioLabel: value,
              }}
              className="w-auto"
              infoRender={field.infoRender}
            />
          ))}
        </div>
      </div>
    ),
    string: (
      <div className={`${baseStyle}`}>
        <Input
          title={field.title}
          label={`${field.title} ${required ? '*' : ''}`}
          key={`${name}`} // Use unique keys for map items
          type="text"
          name={name}
          settings={{
            placeholder: field.decoration?.placeholder || '',
            ...(required && { hint: '*required' }),
          }}
          className="w-auto"
          infoRender={field.infoRender}
        />
      </div>
    ),
    'single-select': (
      <div className={`${baseStyle}`}>
        <Input
          type="select"
          name={name}
          label={`${field.title} ${required ? '*' : ''}`}
          settings={{
            isMulti: false,
            id: 'preferred_contact',
            placeholder: 'Please select',
            options: field.options?.map((value: string) => ({
              value: value,
              name: value,
              label: value,
            })),
          }}
          infoRender={field.infoRender}
        />
      </div>
    ),
    boolean: (
      <div
        className={`flex w-full items-center gap-2 rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-gray-900`}
      >
        <div className="grow">{t(field.title)}</div>
        <Toggle
          name={name}
          checked={(props.value as boolean) ?? false} // Ensure initialValue is used correctly
          onChange={() => {
            const props = getFieldProps(name);
            setFieldValue(name, !props.value);
          }}
        />
      </div>
    ),
  };

  return switchInput[field.type] ?? null;
};
